import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import VehicleServiceHero from '../../../components/ui/VehicleServiceHero';
import { CheckCircle, Wrench, Shield, Clock, DollarSign, Award, Car, Settings, AlertTriangle, Star, Phone, MapPin, Droplet } from 'lucide-react';
import { Link } from 'react-router-dom';
import RelatedInventory from '../../../components/RelatedInventory';

const OilChangeService = () => {
  const oilFeatures = [
    'Synthetic & Conventional Oil',
    'All Vehicle Makes',
    'Multi-Point Inspection',
    'Filter Replacement',
    'Fluid Top-Offs',
    'Quick Service'
  ];

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const oilTypes = [
    {
      type: 'Conventional Oil Change',
      includes: [
        'Up to 5 quarts conventional oil',
        'New oil filter',
        'Multi-point vehicle inspection',
        'Fluid top-offs',
        'Tire pressure check'
      ]
    },
    {
      type: 'Full Synthetic Oil Change',
      includes: [
        'Up to 5 quarts full synthetic oil',
        'Premium oil filter',
        'Multi-point inspection',
        'All fluid top-offs',
        'Battery test',
        'Tire rotation (if needed)'
      ]
    },
    {
      type: 'High-Mileage Oil Change',
      includes: [
        'Up to 5 quarts high-mileage oil',
        'Quality oil filter',
        'Multi-point inspection',
        'Seal conditioner additives',
        'All fluid checks'
      ]
    },
    {
      type: 'European Synthetic Oil Change',
      includes: [
        'European-spec synthetic oil',
        'OEM or premium filter',
        'Complete inspection',
        'Service light reset',
        'All manufacturer requirements'
      ]
    }
  ];

  const oilChangeIntervals = {
    conventional: {
      miles: '3,000-5,000',
      months: '3-6',
      conditions: 'Normal driving conditions'
    },
    synthetic: {
      miles: '7,500-10,000',
      months: '6-12',
      conditions: 'Most driving conditions'
    },
    severe: {
      miles: '3,000',
      months: '3',
      conditions: 'Extreme temperatures, dusty conditions, towing, stop-and-go traffic'
    }
  };

  const benefits = [
    {
      title: 'Engine Protection',
      description: 'Clean oil reduces friction and wear on engine components',
      icon: Shield
    },
    {
      title: 'Improved Performance',
      description: 'Fresh oil helps your engine run smoothly and efficiently',
      icon: Settings
    },
    {
      title: 'Better Fuel Economy',
      description: 'Clean oil reduces engine drag, improving gas mileage',
      icon: DollarSign
    },
    {
      title: 'Extended Engine Life',
      description: 'Regular oil changes prevent premature engine wear',
      icon: Clock
    }
  ];

  return (
    <>
      <Helmet>
        <title>Oil Change Service Dallas | Quick Lube & Filter Replacement | Paradise-Performance</title>
        <meta name="description" content="Professional oil change service in Dallas. Synthetic & conventional oil, all makes & models. Multi-point inspection included. Call (817) 382-2316." />
        <link rel="canonical" href="https://paradise-performance.com/services/oil-change" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AutoRepair",
            "name": "Paradise-Performance - Oil Change Service",
            "url": "https://paradise-performance.com/services/oil-change",
            "description": "Professional oil change service in Dallas. Synthetic & conventional oil options for all vehicle makes.",
            "telephone": "(817) 382-2316",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "305 Co Rd 3470",
              "addressLocality": "Paradise",
              "addressRegion": "TX",
              "postalCode": "76073"
            },
            "areaServed": "Dallas-Fort Worth"
          })}
        </script>
      </Helmet>

      <VehicleServiceHero
        brand="Oil Change Service"
        brandColor="#10B981"
        tagline="Professional Oil Change & Filter Service"
        description="Keep your engine running smoothly with regular oil changes. Quick service, quality oils, all vehicle makes."
        features={oilFeatures}
        heroImage="https://images.unsplash.com/photo-1615906655593-ad0386982a0f?auto=format&fit=crop&w=2000"
        heroImageAlt="Oil Change Service"
      />

      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4">
          {/* Introduction */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Professional Oil Change Service in Dallas</h2>
            
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <p className="text-lg text-gray-700 mb-4">
                  Regular oil changes are the most important maintenance service for your vehicle. Clean oil 
                  keeps your engine running smoothly, prevents wear, and extends the life of your vehicle. At 
                  Paradise-Performance, we provide quick, professional oil changes for all makes and models.
                </p>
                <p className="text-lg text-gray-700 mb-4">
                  Our comprehensive oil change service includes more than just draining and refilling oil. We 
                  perform a multi-point inspection, check all fluid levels, test your battery, and inspect 
                  critical components to keep your vehicle running safely.
                </p>
              </div>
              <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded">
                <Droplet className="w-12 h-12 text-green-600 mb-4" />
                <h3 className="text-xl font-semibold mb-3">Why Regular Oil Changes Matter</h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2" />
                    <span>Removes dirt and debris from engine</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2" />
                    <span>Maintains proper lubrication</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2" />
                    <span>Prevents overheating</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2" />
                    <span>Improves gas mileage</span>
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Oil Change Packages */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Oil Change Packages</h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {oilTypes.map((oil, index) => (
                <div key={index} className="bg-white rounded-lg shadow-lg p-6">
                  <h3 className="text-xl font-semibold mb-4">{oil.type}</h3>
                  <ul className="space-y-2">
                    {oil.includes.map((item, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                        <span className="text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Oil Change Intervals */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">When Should You Change Your Oil?</h2>
            
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="p-6">
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="border-l-4 border-gray-400 pl-4">
                    <h3 className="text-lg font-semibold mb-2">Conventional Oil</h3>
                    <p className="text-2xl font-bold text-gray-700">{oilChangeIntervals.conventional.miles} miles</p>
                    <p className="text-sm text-gray-600">or every {oilChangeIntervals.conventional.months} months</p>
                    <p className="text-sm text-gray-500 mt-2">{oilChangeIntervals.conventional.conditions}</p>
                  </div>
                  
                  <div className="border-l-4 border-green-500 pl-4">
                    <h3 className="text-lg font-semibold mb-2">Synthetic Oil</h3>
                    <p className="text-2xl font-bold text-green-600">{oilChangeIntervals.synthetic.miles} miles</p>
                    <p className="text-sm text-gray-600">or every {oilChangeIntervals.synthetic.months} months</p>
                    <p className="text-sm text-gray-500 mt-2">{oilChangeIntervals.synthetic.conditions}</p>
                  </div>
                  
                  <div className="border-l-4 border-yellow-500 pl-4">
                    <h3 className="text-lg font-semibold mb-2">Severe Conditions</h3>
                    <p className="text-2xl font-bold text-yellow-600">{oilChangeIntervals.severe.miles} miles</p>
                    <p className="text-sm text-gray-600">or every {oilChangeIntervals.severe.months} months</p>
                    <p className="text-sm text-gray-500 mt-2">{oilChangeIntervals.severe.conditions}</p>
                  </div>
                </div>
                
                <div className="mt-8 p-4 bg-yellow-50 rounded">
                  <p className="text-gray-800">
                    <strong>Dallas Driving Tip:</strong> Our hot summers and stop-and-go traffic qualify as 
                    "severe conditions." Consider more frequent oil changes to protect your engine.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Multi-Point Inspection */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Free Multi-Point Inspection Included</h2>
            <p className="text-lg text-gray-700 mb-8">
              With every oil change, we perform a comprehensive inspection of your vehicle to identify 
              potential issues before they become expensive repairs.
            </p>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="font-semibold mb-3">Under the Hood</h3>
                <ul className="space-y-1 text-sm text-gray-600">
                  <li>• Battery condition</li>
                  <li>• Belt & hose inspection</li>
                  <li>• Air filter check</li>
                  <li>• Coolant level & condition</li>
                  <li>• Brake fluid level</li>
                  <li>• Power steering fluid</li>
                </ul>
              </div>
              
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="font-semibold mb-3">Exterior & Tires</h3>
                <ul className="space-y-1 text-sm text-gray-600">
                  <li>• Tire tread depth</li>
                  <li>• Tire pressure check</li>
                  <li>• Brake pad thickness</li>
                  <li>• Wiper blade condition</li>
                  <li>• Exterior light check</li>
                  <li>• Windshield condition</li>
                </ul>
              </div>
              
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="font-semibold mb-3">Under Vehicle</h3>
                <ul className="space-y-1 text-sm text-gray-600">
                  <li>• Exhaust system check</li>
                  <li>• Suspension inspection</li>
                  <li>• CV joint & boots</li>
                  <li>• Transmission fluid</li>
                  <li>• Differential fluid</li>
                  <li>• Leak detection</li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Benefits */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Benefits of Regular Oil Changes</h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="bg-white rounded-lg shadow-lg p-6 text-center">
                  <benefit.icon className="w-12 h-12 text-green-600 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">{benefit.title}</h3>
                  <p className="text-gray-600 text-sm">{benefit.description}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* European Car Oil Changes */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">European Car Oil Change Specialists</h2>
            <p className="text-lg text-gray-700 mb-6">
              European vehicles require specific oil specifications that most quick lube shops don't stock. Using the wrong oil in your BMW, Audi, Mercedes or Porsche can cause timing chain issues, VANOS problems, and void your warranty.
            </p>
            
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">BMW Oil Specifications</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span><strong>LL-01:</strong> Required for most gasoline BMW engines</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span><strong>LL-04:</strong> For diesel engines and some newer models</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>We use Liqui Moly or Castrol Edge—factory approved</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Proper CBS service reset included</span>
                  </li>
                </ul>
                <Link to="/services/european-car-repair/bmw" className="inline-block mt-4 text-blue-600 hover:underline font-medium">
                  Learn more about BMW service →
                </Link>
              </div>
              
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Audi/VW Oil Specifications</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span><strong>VW 502.00:</strong> Standard gasoline engines</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span><strong>VW 504.00/507.00:</strong> Newer engines with DPF</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Critical for DSG and S-Tronic transmission longevity</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>VCDS service reset performed correctly</span>
                  </li>
                </ul>
                <Link to="/services/european-car-repair/audi" className="inline-block mt-4 text-blue-600 hover:underline font-medium">
                  Learn more about Audi service →
                </Link>
              </div>
              
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Mercedes-Benz Oil Specifications</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span><strong>MB 229.5:</strong> Most gasoline engines</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span><strong>MB 229.51:</strong> Diesel engines with DPF</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>AMG engines may require specific formulations</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>ASSYST Plus service reset included</span>
                  </li>
                </ul>
                <Link to="/services/european-car-repair/mercedes" className="inline-block mt-4 text-blue-600 hover:underline font-medium">
                  Learn more about Mercedes service →
                </Link>
              </div>
              
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Porsche Oil Specifications</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span><strong>Porsche A40:</strong> Standard for most models</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span><strong>Porsche C30:</strong> Cayenne diesel models</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>GT3/GT4 engines require specific high-temp formulas</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>PIWIS diagnostic service reset included</span>
                  </li>
                </ul>
                <Link to="/services/european-car-repair/porsche" className="inline-block mt-4 text-blue-600 hover:underline font-medium">
                  Learn more about Porsche service →
                </Link>
              </div>
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-600 p-6 rounded-r-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Why European Cars Need Specialty Oil</h3>
              <p className="text-gray-700 mb-4">
                European engines run hotter, have tighter tolerances, and use longer oil change intervals than American or Japanese vehicles. The oil specifications (LL-01, VW 502.00, MB 229.5, etc.) aren't marketing gimmicks—they're engineering requirements.
              </p>
              <p className="text-gray-700">
                Using conventional or non-spec synthetic oil can cause premature timing chain wear (especially on BMW N20/N55 engines), carbon buildup in direct injection engines, and catalytic converter damage. The $30 you save on a cheap oil change isn't worth a $4,000 timing chain repair.
              </p>
            </div>
          </motion.div>

          {/* Why Choose Us */}
          <motion.div {...fadeInUp} className="mb-16 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg p-8">
            <h2 className="text-3xl font-bold mb-6">Why Choose Paradise-Performance for Your Oil Change?</h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="h-6 w-6 mr-3 flex-shrink-0" />
                    <div>
                      <strong>Quick Service:</strong> Most oil changes completed in 30 minutes or less
                    </div>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-6 w-6 mr-3 flex-shrink-0" />
                    <div>
                      <strong>No Appointment Necessary:</strong> Walk-ins welcome, or schedule ahead
                    </div>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-6 w-6 mr-3 flex-shrink-0" />
                    <div>
                      <strong>Quality Products:</strong> Name-brand oils and filters only
                    </div>
                  </li>
                </ul>
              </div>
              <div>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="h-6 w-6 mr-3 flex-shrink-0" />
                    <div>
                      <strong>Honest Service:</strong> We'll never sell you services you don't need
                    </div>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-6 w-6 mr-3 flex-shrink-0" />
                    <div>
                      <strong>Service Records:</strong> We maintain your service history
                    </div>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-6 w-6 mr-3 flex-shrink-0" />
                    <div>
                      <strong>Competitive Pricing:</strong> Fair prices with no hidden fees
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div {...fadeInUp} className="text-center bg-gray-900 text-white rounded-xl p-12">
            <h2 className="text-3xl font-bold mb-4">Schedule Your Oil Change Today</h2>
            <p className="text-xl mb-8 text-gray-300">
              Quick, professional oil changes for all makes and models
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="tel:8173822316" 
                className="inline-block px-8 py-4 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700 transition-colors"
              >
                <Phone className="inline-block w-5 h-5 mr-2" />
                Call (817) 382-2316
              </a>
              <Link 
                to="/contact" 
                className="inline-block px-8 py-4 bg-white text-gray-900 font-bold rounded-lg hover:bg-gray-100 transition-colors"
              >
                Schedule Online
              </Link>
            </div>
            
            <div className="mt-8 pt-8 border-t border-gray-700">
              <p className="text-gray-400">
                <MapPin className="inline-block w-4 h-4 mr-1" />
                305 Co Rd 3470, Paradise, TX 76073 • Open Monday-Friday 8am-5pm
              </p>
            </div>
          </motion.div>
        </div>
      {/* Vehicle Inventory Links */}
      <div className="max-w-7xl mx-auto px-4 pb-12">
        <RelatedInventory limit={4} title="Shop Our Inspected Vehicles" />
      </div>
      </div>

    </>
  );
};

export default OilChangeService;
